// Allow-list of React Query cache entries that may be persisted to IndexedDB for offline use.
//
// Only read-only data a coach / student needs offline is persisted. Billing (bills, payments, balance, price
// previews, resignation previews), admin lists and statistics are NEVER persisted — they must always come
// fresh from the server.
//
// Custom hooks (`packages/shared-logic/src/hooks/*`) use `['<name>', schoolId, ...]` keys; Orval generated hooks
// use the request URL as the first key element (`/api/v1/schools/${schoolId}/...`, optional params object next).
import type { QueryKey } from '@tanstack/react-query';

/** Custom-hook keys `[name, schoolId, ...]` persisted as a whole family. */
const PERSISTED_CUSTOM_KEYS = new Set([
  'sessions', //       useSessions ['sessions', schoolId, filters], useSession ['sessions', schoolId, 'detail', id]
  'locations', //      useLocations ['locations', schoolId]
  'skill-levels', //   useSkillLevels ['skill-levels', schoolId]
  'my-waitlists', //   useListMyWaitlists ['my-waitlists', schoolId, params]
  'subscriptions', //  useStudentSubscriptions ['subscriptions', schoolId, studentMemberId] (read-only display)
  'credits', //        useStudentCredits ['credits', schoolId, studentMemberId] (read-only display)
]);

/** Orval URL keys, matched against the path after `/api/v1/schools/{schoolId}`. */
const PERSISTED_URL_PATTERNS: RegExp[] = [
  /^\/me$/, //                                     useGetUserContext (own identity + roles; needed for an offline cold start)
  /^\/attendance\/session\/[^/]+$/, //             useGetAttendanceForSession
  /^\/coaches\/public$/, //                        useListCoachesPublic
  /^\/credits\/student\/[^/]+\/usable$/, //        useListUsableStudentCredits (read-only display)
];

const SCHOOL_URL_RE = /^\/api\/v1\/schools\/[^/]+(\/.*)$/;

/** True when a query with this key may be written to the persisted (IndexedDB) query cache. */
export function isPersistableQueryKey(queryKey: QueryKey): boolean {
  const head = queryKey[0];
  if (typeof head !== 'string') return false;

  if (head.startsWith('/')) {
    const match = SCHOOL_URL_RE.exec(head);
    return !!match && PERSISTED_URL_PATTERNS.some((re) => re.test(match[1]));
  }

  // Every custom key below is school-scoped: `[name, schoolId, ...]`.
  if (typeof queryKey[1] !== 'string' || queryKey[1] === '') return false;

  if (head === 'schools') return queryKey.length === 2; // useSchool ['schools', schoolId] (not the master-admin list)
  if (head === 'enrollments') {
    // ['enrollments', 'calculate-price', ...] is a price preview; ['enrollments', schoolId, 'all', ...] an admin list.
    return queryKey[1] !== 'calculate-price' && queryKey[2] !== 'all';
  }
  return PERSISTED_CUSTOM_KEYS.has(head);
}
